import { Component, Input, OnInit, Output, EventEmitter, ChangeDetectionStrategy, inject } from '@angular/core';
import { PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { formatDate } from '../../shared/utils';
import { FormattingPipe } from '../formatting-pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AngularSvgIconModule } from 'angular-svg-icon';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CreateActivityDialogComponent } from './create-activity-dialog/create-activity-dialog.component';
import { ComponentsService } from 'src/app/services/api/components.service';
import { DevicesService } from 'src/app/services/api/devices.service';
import { ResponseHandlerService } from 'src/app/services/responseHandler.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ZXComponent } from 'src/app/models/component';
import { BreakpointService } from 'src/app/services/breakpoint.service';

export enum ActivityType {
  Created = 1,
  Added = 2,
  Available = 3,
  InUse = 4,
  PreferencesUpdated = 5,
  SettingsUpdated = 6,
  Battery = 7,
  Shock = 8,
  Incidents = 9,
  Acknowledge = 10,
  Reopened = 11,
  Archived = 12,
  InService = 13,
  InReturn = 14,
  BatteryChanged = 15,
  Inspected = 16,
  Default,
}

export class ComponentActivity {
  text: string;
  timestamp: Date;
  activity: ActivityType;
}

@Component({
  selector: 'app-recent-history',
  templateUrl: './recent-history.component.html',
  styleUrls: ['./recent-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatCardModule,
    MatPaginatorModule,
    AngularSvgIconModule,
    MatProgressSpinnerModule,
    TranslatePipe,
    FormattingPipe,
    MatButtonModule,
    MatDialogModule,
  ],
})
export class RecentHistoryComponent implements OnInit {
  private _timeline: ComponentActivity[] | undefined = [];
  @Input() set timeline(time: ComponentActivity[] | undefined) {
    this._timeline = time;
    this.initializePagination();
  }

  get timeline() {
    return this._timeline;
  }

  displayedTimeLine: ComponentActivity[] | undefined = [];

  @Input() title: string;
  @Input() isHistoryLoaded = false;
  @Input() showCreateActivityButton = false;
  @Input() component: ZXComponent;
  @Output() activityCreated = new EventEmitter<void>();

  /** Phones show fewer entries per page so the card stays short; the paginator reaches the rest. */
  pageSize = inject(BreakpointService).isPhone() ? 3 : 8;
  activityLength = 0;

  constructor(
    private dialog: MatDialog,
    private componentsService: ComponentsService,
    private devicesService: DevicesService,
    private responseHandler: ResponseHandlerService
  ) {}

  ngOnInit(): void {
    this.initializePagination();
  }

  initializePagination() {
    this.displayedTimeLine = this._timeline?.slice(0, this.pageSize);
  }

  triggerPageChange(event: PageEvent): void {
    // FE pagination for activity
    const start = this.pageSize * event.pageIndex;
    const end = this.pageSize * (event.pageIndex + 1);
    this.displayedTimeLine = this._timeline?.slice(start, end);
  }

  // "repeater" to make it available in template
  formatDate(date: Date): string {
    return formatDate(date);
  }

  openCreateActivityModal(): void {
    const dialogRef = this.dialog.open(CreateActivityDialogComponent, {
      data: {
        component: this.component,
      },
    });

    dialogRef.afterClosed().subscribe((data: [string, Date]) => {
      if (!data) {
        return;
      }

      switch (data[0]) {
        case 'batteryChange':
          this.devicesService.batteryChange(this.component.deviceIdentifier, data[1]).subscribe({
            next: () => this.activityCreated.emit(),
            error: (error: HttpErrorResponse) => {
              this.responseHandler.error(error);
            },
          });
          break;
        case 'inspect':
          this.componentsService.inspect(this.component.id, data[1]).subscribe({
            next: () => this.activityCreated.emit(),
            error: (error: HttpErrorResponse) => {
              this.responseHandler.error(error);
            },
          });
          break;
      }
    });
  }

  getActivityIcon(activity: ActivityType): string {
    switch (activity) {
      case ActivityType.Added:
      case ActivityType.Created:
        return '/assets/icons/icon-40-activity-log-add.svg';
      case ActivityType.Available:
        return '/assets/icons/icon-40-activity-log-available.svg';
      case ActivityType.InUse:
        return '/assets/icons/icon-40-activity-log-in-use.svg';
      case ActivityType.PreferencesUpdated:
        return '/assets/icons/icon-40-activity-log-preferences-update.svg';
      case ActivityType.SettingsUpdated:
        return '/assets/icons/icon-activity-log-preferences-update.svg';
      case ActivityType.Battery:
        return '/assets/icons/icon-40-activity-log-low-battery.svg';
      case ActivityType.Shock:
        return '/assets/icons/icon-40-activity-log-shock.svg';
      case ActivityType.Incidents:
        return '/assets/icons/icon-40-activity-log-base.svg';
      case ActivityType.Acknowledge:
        return '/assets/icons/icon-40-activity-log-acknowledged.svg';
      case ActivityType.Reopened:
        return '/assets/icons/icon-40-activity-log-reopened.svg';
      case ActivityType.Archived:
        return '/assets/icons/icon-40-activity-log-archived.svg';
      case ActivityType.InReturn:
        return '/assets/icons/icon-40-activity-log-return.svg';
      case ActivityType.InService:
        return '/assets/icons/icon-40-activity-log-in-service.svg';
      case ActivityType.BatteryChanged:
        return '/assets/icons/icon-40-activity-log-battery-changed.svg';
      case ActivityType.Inspected:
        return '/assets/icons/icon-40-activity-log-inspected.svg';
      case ActivityType.Default:
        return '/assets/icons/icon-40-activity-log-reopened.svg';
    }
  }
}
